/**
 * CHG-053: 이슈 이동 + 계층 오버라이드 훅
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as moveApi from '../services/issueMoveService';
import { projectIssueTypeKeys } from './useProjectIssueTypes';
import { configKeys } from './useProjectIssueTypeConfig';

export const hierarchyKeys = {
  overrides: (projectId: string) => ['project-hierarchy-overrides', projectId] as const,
};

export function useHierarchyOverrides(projectId: string) {
  return useQuery({
    queryKey: hierarchyKeys.overrides(projectId),
    queryFn: () => moveApi.fetchHierarchyOverrides(projectId),
    enabled: !!projectId,
  });
}

export function useAddHierarchyOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; data: { parentTypeId: string | null; childTypeId: string; action: 'ADD' | 'REMOVE' } }) =>
      moveApi.addHierarchyOverride(params.projectId, params.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: hierarchyKeys.overrides(vars.projectId) });
      qc.invalidateQueries({ queryKey: projectIssueTypeKeys.all(vars.projectId) });
      qc.invalidateQueries({ queryKey: configKeys.config(vars.projectId) });
    },
  });
}

export function useRemoveHierarchyOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; overrideId: string }) =>
      moveApi.removeHierarchyOverride(params.projectId, params.overrideId),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: hierarchyKeys.overrides(vars.projectId) });
      qc.invalidateQueries({ queryKey: projectIssueTypeKeys.all(vars.projectId) });
      qc.invalidateQueries({ queryKey: configKeys.config(vars.projectId) });
    },
  });
}

export function useMovePreview() {
  return useMutation({
    mutationFn: (params: { issueId: string; targetProjectId: string }) =>
      moveApi.movePreview(params.issueId, params.targetProjectId),
  });
}

export function useMoveIssue() {
  return useMutation({
    mutationFn: (params: { issueId: string; data: { targetProjectId: string; targetIssueTypeId: string; targetStatusId: string; keepCustomFields?: boolean } }) =>
      moveApi.moveIssue(params.issueId, params.data),
  });
}
